import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { Add, Close, Delete } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_URL = "/api/v1/product-notes";

const ProductNote = ({ productId }) => {
  const [notes, setNotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    note: "",
    noteLocation: "background",
  });

  // ================= FETCH NOTES =================
  const fetchNotes = async () => {
    try {
      const res = await axios.get(`${API_URL}/get-product-note/${productId}`);
      setNotes(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch notes");
    }
  };

  useEffect(() => {
    if (productId) fetchNotes();
  }, [productId]);

  // ================= ADD NOTE =================
  const submitNote = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/create-product-note`, {
        productId,
        note: formData.note,
        noteLocation: formData.noteLocation,
      });

      toast.success("Note added");
      setShowForm(false);
      setFormData({ note: "", noteLocation: "background" });
      fetchNotes();
    } catch (err) {
      console.error(err);
      toast.error("Failed to add note");
    }
  };

  // ================= DELETE NOTE =================
  const deleteNote = async (id) => {
    try {
      await axios.delete(`${API_URL}/delete-product-note/${id}`);
      toast.success("Note deleted");
      fetchNotes();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete note");
    }
  };

  // ================= FILTER =================
  const yearNotes = notes.filter(
    (n) => n.noteLocation === "product_year_wise_note"
  );
  const otherNotes = notes.filter(
    (n) => n.noteLocation !== "product_year_wise_note"
  );

  return (
    <Box maxWidth={700} mx="auto" py={3}>
      <ToastContainer />
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Product Notes
      </Typography>

      {!showForm ? (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setShowForm(true)}
        >
          Add Note
        </Button>
      ) : (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Add Note</Typography>
              <IconButton onClick={() => setShowForm(false)}>
                <Close />
              </IconButton>
            </Stack>

            <form onSubmit={submitNote}>
              <ReactQuill
                theme="snow"
                value={formData.note}
                onChange={(v) => setFormData({ ...formData, note: v })}
                modules={{
                  toolbar: [
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    ["link"],
                    ["clean"],
                  ],
                }}
              />

              <TextField
                select
                fullWidth
                sx={{ mt: 2 }}
                label="Note Location"
                value={formData.noteLocation}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    noteLocation: e.target.value,
                  })
                }
              >
                <MenuItem value="background">Background</MenuItem>
                <MenuItem value="header">Header</MenuItem>
                <MenuItem value="footer">Footer</MenuItem>
                <MenuItem value="product_year_wise_note">
                  Product Year Wise Note
                </MenuItem>
              </TextField>

              <Stack direction="row" justifyContent="flex-end" mt={2}>
                <Button type="submit" variant="contained">
                  Save
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      )}

      {/* ================= NOTES LIST ================= */}
      <Box mt={3}>
        {/* Other Notes */}
        {otherNotes.map((n) => (
          <Card key={n.id} sx={{ mb: 2 }}>
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box
                  sx={{
                    "& p": { margin: 0, padding: 0 },
                    "& a": {
                      color: "#1976d2",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: "bold",
                    },
                    "& ul, & ol": { paddingLeft: "20px" },
                  }}
                >
                  <Typography
                    component="div"
                    dangerouslySetInnerHTML={{ __html: n.note }}
                  />

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    mt={1}
                  >
                    Location: {n.noteLocation} |{" "}
                    {new Date(
                      n.created_at || Date.now()
                    ).toLocaleDateString()}
                  </Typography>
                </Box>

                <IconButton color="error" onClick={() => deleteNote(n.id)}>
                  <Delete />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        ))}

        {/* Year Wise Section */}
        {yearNotes.length > 0 && (
          <Typography variant="h6" mt={3}>
            Product Year Wise Notes
          </Typography>
        )}

        {yearNotes.length === 0 && (
          <Typography color="text.secondary" mt={2}>
            No product year wise notes yet
          </Typography>
        )}

        {yearNotes.map((n) => (
          <Card key={n.id} sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between">
                <Box>
                  <Typography
                    component="div"
                    dangerouslySetInnerHTML={{ __html: n.note }}
                  />

                  <Typography variant="caption">
                    {new Date(
                      n.created_at || Date.now()
                    ).toLocaleDateString()}
                  </Typography>
                </Box>

                <IconButton color="error" onClick={() => deleteNote(n.id)}>
                  <Delete />
                </IconButton>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default ProductNote;
